import { Fragment, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { Panel, PanelHeader } from '../ui/Panel';
import Button from '../ui/Button';
import { fmt } from '../../lib/format';
import { EventListItem, FORMAT_LABEL, KIND_LABEL, eventDates, torontoToday } from '../../lib/events';
import { em, headRow, num, td as tdBase, th } from './logistics';

const lastDay = (e: EventListItem) => e.days[e.days.length - 1]?.date ?? String(e.endsAt).slice(0, 10);
const firstDay = (e: EventListItem) => e.days[0]?.date ?? String(e.startsAt).slice(0, 10);
const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86400000);
const coming = (e: EventListItem) =>
  (e.byStatus.registered ?? 0) + (e.byStatus.confirmed ?? 0) + (e.byStatus.attended ?? 0);
const attendedOf = (e: EventListItem) =>
  e.attendedFromCounts ? (e.attended ?? 0) : Math.max(e.attended ?? 0, e.checkedIn);
/** "the 4th Annual …" without doubling a title that already starts with "The". */
const the = (title: string) => (/^the\s/i.test(title) ? title : `the ${title}`);
const KIND_SHORT: Record<string, string> = { national: 'National meeting', regional: 'Regional meeting' };

/** "in 28 days", "tomorrow", "today", "on now" for an event that has not ended. */
export function whenFromToday(e: EventListItem, today = torontoToday()) {
  const n = daysBetween(today, firstDay(e));
  if (n < 0) return 'on now';
  if (n === 0) return 'today';
  if (n === 1) return 'tomorrow';
  return `in ${fmt(n)} days`;
}

/** Meetings that have not ended (outreach talks and PI meetings on their own are listed in their own sections). */
const meetingsOf = (events: EventListItem[]) => events.filter((e) => e.kind !== 'outreach' && e.kind !== 'pi_meeting');
const upcomingOf = (events: EventListItem[], today: string) =>
  meetingsOf(events)
    .filter((e) => lastDay(e) >= today)
    .sort((a, b) => firstDay(a).localeCompare(firstDay(b)));
const pastOf = (events: EventListItem[], today: string) =>
  meetingsOf(events)
    .filter((e) => lastDay(e) < today)
    .sort((a, b) => firstDay(b).localeCompare(firstDay(a)));

/** The page's opening sentence: what is coming up next, with real numbers. */
export function listLead(events: EventListItem[]): string {
  const today = torontoToday();
  const upcoming = upcomingOf(events, today);
  const next = upcoming[0];
  if (!next) {
    const last = pastOf(events, today)[0];
    return last
      ? `No event is coming up. The last was ${the(last.title)}, ${eventDates(last)}.`
      : 'No event is coming up.';
  }
  const when = whenFromToday(next, today);
  if (upcoming.length > 1)
    return `${fmt(upcoming.length)} events are coming up; the next is ${the(next.title)}, ${when}.`;
  const n = coming(next);
  return `One event is coming up: ${the(next.title)}, ${when}. ${
    n === 1 ? 'One person is' : `${fmt(n)} people are`
  } coming so far.`;
}

// Tables that become one block per row below 768px; each cell shows its column name first.
const table = 'w-full text-table tabular max-md:block';
const thead = 'max-md:hidden';
const tbody = 'max-md:block';
const tr = 'border-b border-line hover:bg-selected max-md:block max-md:py-3 max-md:hover:bg-transparent';
const td = `${tdBase} max-md:block max-md:py-[2px] max-md:pr-0 max-md:text-left`;
const labelled = `max-md:before:content-[attr(data-label)_'_·_'] max-md:before:text-small max-md:before:text-ink-3`;
const dash = <span className="text-ink-3">–</span>;
const count = (n: number | null | undefined) => (n ? fmt(n) : dash);
const titleLink = 'font-serif font-semibold text-[18px] leading-[26px] text-ink no-underline hover:underline';

function Cell({ label, className = '', children }: { label?: string; className?: string; children?: ReactNode }) {
  return (
    <td className={`${td} ${label ? labelled : ''} ${className}`} data-label={label}>
      {children}
    </td>
  );
}

/** "National meeting · with the PI dinner, Sun, Nov 29, 2026" (sub-events linked). */
function KindLine({ e, kind = true }: { e: EventListItem; kind?: boolean }) {
  const subs = e.subEventList ?? [];
  const label = KIND_SHORT[e.kind] ?? KIND_LABEL[e.kind] ?? e.kind;
  if (!kind && !subs.length) return null;
  return (
    <span className={em}>
      {kind ? label : null}
      {subs.length ? (
        <>
          {kind ? ' · ' : ''}with{' '}
          {subs.map((s, i) => (
            <Fragment key={s.id}>
              {i ? (i === subs.length - 1 ? ' and ' : ', ') : ''}
              <Link to={`/events/${s.id}`}>
                {/^the\s/i.test(s.title) ? '' : 'the '}
                {s.title}, {eventDates({ days: s.days, outreach: null })}
              </Link>
            </Fragment>
          ))}
        </>
      ) : null}
    </span>
  );
}

function ComingUp({ events }: { events: EventListItem[] }) {
  return (
    <Panel>
      <PanelHeader title="Coming up" />
      <table className={table}>
        <thead className={thead}>
          <tr className={headRow}>
            <th className={`${th} w-[220px]`}>When</th>
            <th className={th}>Event</th>
            <th className={th}>Where</th>
            <th className={`${th} ${num}`}>On the list</th>
            <th className={`${th} ${num}`}>Coming</th>
            <th className={`${th} ${num}`}>Not attending</th>
          </tr>
        </thead>
        <tbody className={tbody}>
          {events.map((e) => (
            <tr key={e.id} className={tr}>
              <Cell label="When">
                {eventDates(e)}
                <span className={em}>{whenFromToday(e)}</span>
              </Cell>
              <Cell>
                <Link to={`/events/${e.id}`} className={titleLink}>
                  {e.title}
                </Link>
                <KindLine e={e} />
              </Cell>
              <Cell label="Where">{e.city || dash}</Cell>
              <Cell label="On the list" className={num}>
                {fmt(e.invitees)}
              </Cell>
              <Cell label="Coming" className={num}>
                {fmt(coming(e))}
              </Cell>
              <Cell label="Not attending" className={num}>
                {fmt(e.byStatus.declined ?? 0)}
              </Cell>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

type KindFilter = 'all' | 'national' | 'regional';

function PastMeetings({ events }: { events: EventListItem[] }) {
  const [kind, setKind] = useState<KindFilter>('all');
  const shown = events.filter((e) => kind === 'all' || e.kind === kind);
  const counted = shown.some((e) => e.attendedFromCounts);
  const segments: [KindFilter, string][] = [
    ['all', 'All'],
    ['national', 'National'],
    ['regional', 'Regional'],
  ];
  return (
    <Panel>
      <PanelHeader title="Past meetings" note={fmt(shown.length)}>
        <div
          className="inline-flex rounded-sm border border-line-strong overflow-hidden"
          role="group"
          aria-label="Show"
        >
          {segments.map(([k, l], i) => (
            <button
              key={k}
              type="button"
              aria-pressed={kind === k}
              onClick={() => setKind(k)}
              className={`min-h-[40px] px-3 py-2 text-[15px] leading-5 ${i ? 'border-l border-line-strong' : ''} ${
                kind === k ? 'bg-ink text-paper font-semibold' : 'text-ink hover:bg-selected'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </PanelHeader>
      {!shown.length ? (
        <p className="text-[15px] leading-[22px] text-ink-2 py-3">
          No {kind === 'national' ? 'national' : 'regional'} meeting has ended yet.
        </p>
      ) : (
        <table className={table}>
          <thead className={thead}>
            <tr className={headRow}>
              <th className={`${th} w-[220px]`}>When</th>
              <th className={th}>Event</th>
              <th className={th}>Where</th>
              <th className={`${th} ${num}`}>Attended</th>
              <th className={`${th} ${num}`}>On the list</th>
              <th className={`${th} ${num}`}>No-shows</th>
            </tr>
          </thead>
          <tbody className={tbody}>
            {shown.map((e) => (
              <tr key={e.id} className={tr}>
                <Cell label="When">
                  {eventDates(e)}
                  {e.closedAt ? <span className={em}>Closed</span> : null}
                </Cell>
                <Cell>
                  <Link to={`/events/${e.id}`} className={titleLink}>
                    {e.title}
                  </Link>
                  <KindLine e={e} />
                </Cell>
                <Cell label="Where">{e.city || dash}</Cell>
                <Cell label="Attended" className={num}>
                  {fmt(attendedOf(e))}
                  {e.attendedFromCounts ? <span aria-label="counted without names">*</span> : null}
                </Cell>
                <Cell label="On the list" className={num}>
                  {count(e.invitees)}
                </Cell>
                <Cell label="No-shows" className={num}>
                  {count(e.byStatus.no_show)}
                </Cell>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {counted ? (
        <p className="text-small text-ink-3 mt-3">
          * Counted on the day without names, so there&rsquo;s no list to compare against.
        </p>
      ) : null}
    </Panel>
  );
}

function PiMeetings({ events }: { events: EventListItem[] }) {
  return (
    <Panel>
      <PanelHeader title="PI meetings" note={`${fmt(events.length)} on their own`} />
      <table className={table}>
        <thead className={thead}>
          <tr className={headRow}>
            <th className={`${th} w-[220px]`}>When</th>
            <th className={th}>Meeting</th>
            <th className={th}>Where</th>
            <th className={`${th} ${num}`}>People</th>
          </tr>
        </thead>
        <tbody className={tbody}>
          {events.map((e) => {
            const people = e.counts?.total ?? (e.attended || null);
            return (
              <tr key={e.id} className={tr}>
                <Cell label="When">{eventDates(e)}</Cell>
                <Cell>
                  <Link to={`/events/${e.id}`} className={titleLink}>
                    {e.title}
                  </Link>
                  <KindLine e={e} kind={false} />
                </Cell>
                <Cell label="Where">{e.city || dash}</Cell>
                <Cell label="People" className={num}>
                  {people != null ? fmt(people) : dash}
                </Cell>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-small text-ink-3 mt-3">
        PI meetings held alongside a national meeting are listed with it above.
      </p>
    </Panel>
  );
}

function ofWhom(e: EventListItem) {
  const c = e.counts;
  if (!c) return '';
  const parts = [
    c.trainees != null ? `${fmt(c.trainees)} trainees` : null,
    c.faculty != null ? `${fmt(c.faculty)} faculty` : null,
    c.other ? `${fmt(c.other)} other` : null,
  ]
    .filter(Boolean)
    .join(', ');
  return `${parts}${c.registered != null ? `${parts ? ' · ' : ''}${fmt(c.registered)} registered` : ''}`;
}

function Outreach({ events, onLog }: { events: EventListItem[]; onLog?: () => void }) {
  const [all, setAll] = useState(false);
  const shown = all ? events : events.slice(0, 10);
  const people = events.reduce((s, e) => s + (e.counts?.total ?? 0), 0);
  return (
    <Panel>
      <PanelHeader
        title="Outreach talks and visits"
        note={`${fmt(events.length)} · ${people === 1 ? 'one person' : `${fmt(people)} people`} counted`}
      >
        {onLog ? <Button onClick={onLog}>Log an outreach talk</Button> : null}
      </PanelHeader>
      <table className={table}>
        <thead className={thead}>
          <tr className={headRow}>
            <th className={`${th} w-[220px]`}>When</th>
            <th className={th}>Talk or visit</th>
            <th className={th}>Where</th>
            <th className={th}>Format</th>
            <th className={`${th} ${num}`}>People</th>
            <th className={th}>Of whom</th>
          </tr>
        </thead>
        <tbody className={tbody}>
          {shown.map((e) => {
            const where = [e.outreach?.institution, e.city].filter(Boolean).join(', ');
            const whom = ofWhom(e);
            return (
              <tr key={e.id} className={tr}>
                <Cell label="When">{eventDates(e)}</Cell>
                <Cell>
                  <Link to={`/events/${e.id}`} className="font-semibold text-ink no-underline hover:underline">
                    {e.title}
                  </Link>
                </Cell>
                <Cell label="Where">{where || dash}</Cell>
                <Cell label="Format">{e.outreach?.format ? FORMAT_LABEL[e.outreach.format] : dash}</Cell>
                <Cell label="People" className={num}>
                  {e.counts?.total != null ? fmt(e.counts.total) : dash}
                </Cell>
                <Cell label="Of whom" className={`text-ink-2 ${whom ? '' : 'max-md:hidden'}`}>
                  {whom}
                </Cell>
              </tr>
            );
          })}
        </tbody>
      </table>
      {events.length > 10 ? (
        <Button variant="quiet" className="mt-2 !px-0" onClick={() => setAll(!all)}>
          {all ? 'Show the latest 10' : `Show all ${fmt(events.length)}`}
        </Button>
      ) : null}
    </Panel>
  );
}

/**
 * The events list: meetings coming up (soonest first), past meetings (newest first, by kind), PI meetings held on
 * their own, then outreach talks and visits. Sub-events (a PI dinner held with a national meeting) sit under their
 * meeting.
 */
export default function EventListSections({
  events,
  onLogOutreach,
}: {
  events: EventListItem[];
  /** Admins: opens the outreach dialog from the outreach section's head. */
  onLogOutreach?: () => void;
}) {
  const today = torontoToday();
  const upcoming = upcomingOf(events, today);
  const past = pastOf(events, today);
  const piOnly = events.filter((e) => e.kind === 'pi_meeting').sort((a, b) => firstDay(b).localeCompare(firstDay(a)));
  const outreach = events.filter((e) => e.kind === 'outreach').sort((a, b) => firstDay(b).localeCompare(firstDay(a)));
  return (
    <>
      {upcoming.length ? <ComingUp events={upcoming} /> : null}
      {past.length ? <PastMeetings events={past} /> : null}
      {piOnly.length ? <PiMeetings events={piOnly} /> : null}
      {outreach.length ? <Outreach events={outreach} onLog={onLogOutreach} /> : null}
    </>
  );
}
